import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output
} from '@angular/core';
import { Subscription } from 'rxjs';
import type { IDropdownCart } from './dropdown-cart.interface';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { SdTitleComponent } from '../../title/title.component';

import { SdPushCardComponent } from '../../push-card/push-card.component';
import { PushSizeEnum } from '../../push-card/push-card.enum';
import { IPromo } from '../../product-promo/product-promo.interface';
import { SdProductPromoComponent } from '../../product-promo/product-promo.component';
import { SdNavigationProductCardComponent } from '../../navigation-product-card/navigation-product-card.component';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { CoreModalService } from '../../modals/core-modal/core-modal.service';
import { DeviceService } from '../../../_services/device.service';
import { IconSizeEnum } from '../../../_enums/icon.enum';
import { SdButtonComponent } from '../../buttons/button/button.component';
import { SdButtonIconComponent } from '../../buttons/button-icon/button-icon.component';
import { SdDropdownComponent } from '../../dropdowns/dropdown/dropdown.component';
import { SdLinkComponent } from '../../links/link/link.component';
import { SdListComponent } from '../../lists/list/list.component';
import { SdConvertedPriceComponent } from '../../converted-price/converted-price.component';

@Component({
  selector: 'sd-dropdown-cart',
  templateUrl: './dropdown-cart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdDropdownComponent,
    SdTitleComponent,
    SdListComponent,
    SdNavigationProductCardComponent,
    SdButtonIconComponent,
    SdProductPromoComponent,
    SdButtonComponent,
    RouterLink,
    SdPushCardComponent,
    SdLinkComponent,
    TranslateModule,
    SdConvertedPriceComponent
  ]
})
export class SdDropdownCartComponent implements OnInit, OnDestroy {
  brand = input<BrandsEnum>();
  cartData = input<IDropdownCart>();

  products = computed(() => this.cartData()?.products);

  delete = output<string>();
  empty = output<void>();
  quantity = output<{ product: string; qty: number }>();
  isOpenedChange = output<boolean>();
  promoClick = output<IPromo>();
  openRecommendedPriceModal = output<void>();

  private readonly coreModalService = inject(CoreModalService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly deviceService = inject(DeviceService);

  iconSize = IconSizeEnum;
  pushSizeEnum = PushSizeEnum;
  mobileSubscription: Subscription;
  deviceSubscription: Subscription;
  isMobile$: boolean;
  isDevice$: boolean;
  isOpen = false;

  get totalProducts() {
    if (this.products()?.length > 0) {
      const productsQuantity = this.products()?.map(
        (product) => product.quantity || 1,
      );
      return productsQuantity.reduce(
        (accumulator: number, curr: number) => accumulator + curr,
      );
    } else {
      return 0;
    }
  }

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
      this.isDevice$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
    this.deviceSubscription?.unsubscribe();
  }

  closeDropdown() {
    this.coreModalService.closeAllModal();
  }

  onDelete(productCode: string) {
    this.delete.emit(productCode);
  }

  onEmptyCart() {
    this.empty.emit();
  }

  onQuantityChange(product: string, qty: number) {
    this.quantity.emit({ product, qty });
  }

  onOpenedChange(event: boolean) {
    if (event !== this.isOpen) {
      this.isOpen = event;
      this.isOpenedChange.emit(this.isOpen);
    }
  }

  onPromoClick(promo: IPromo) {
    this.promoClick.emit(promo);
  }

  onOpenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }
}
